import { Alignment, EventType, Fit, Layout, useRive, useStateMachineInput } from '@rive-app/react-canvas';
import type { Event, RiveEventPayload } from '@rive-app/react-canvas';
import { clsx } from 'clsx';
import { useEffect } from 'react';
import { Flex } from '../Flex/Flex';
import * as styles from './Rating.css';
import starRatingRiv from './star-rating.riv';

const STATE_MACHINE_NAME = 'RatingStateMachine';
const RATING_INPUT_NAME = 'Rating';
const RIVE_EVENT_CHANGE_VALUE_NAME = 'AnimationFinished';
const STAR_PROPERTY_NAME = 'star';

// The star-rating file contains a state machine and an input with 5 stars.
// When the user clicks on a star, the state machine is triggered and the input value is set to the star number.
// When the animation finishes, we receive an event named 'AnimationFinished' containing the star number property.
// When setting the value, we use the negative of the value because the state machine is configured
// to use the negative value to avoid triggering the animation.
export interface RatingProps {
  readonly value?: number;
  readonly onChange: (value: number) => void;
  readonly className?: string;
}

export const Rating: React.FC<RatingProps> = ({ value = 0, onChange, className }) => {
  const { rive, RiveComponent } = useRive({
    src: starRatingRiv,
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
    layout: new Layout({
      fit: Fit.FitHeight,
      alignment: Alignment.Center,
    }),
  });

  const ratingInput = useStateMachineInput(rive, STATE_MACHINE_NAME, RATING_INPUT_NAME);

  useEffect(() => {
    /* istanbul ignore next -- @preserve - can't mock undefined ratingInput */
    if (ratingInput == null || value === 0) return;

    ratingInput.value = -value;
    ratingInput.fire();
  }, [value, ratingInput]);

  useEffect(() => {
    if (rive == null) return;

    rive.on(EventType.RiveEvent, (event: Event) => {
      const riveEventPayload = event.data as RiveEventPayload;

      if (riveEventPayload.name === RIVE_EVENT_CHANGE_VALUE_NAME) {
        const newValue = Number(riveEventPayload.properties?.[STAR_PROPERTY_NAME]);
        if (Math.abs(newValue) !== Math.abs(value)) {
          onChange(newValue);
        }
      }
    });

    return () => {
      rive.removeAllRiveEventListeners(EventType.RiveEvent);
    };
  }, [rive, onChange, value]);

  return (
    <Flex className={clsx(styles.ratingContainer, className)}>
      <RiveComponent className={styles.rating} />
    </Flex>
  );
};
